/**
 * API service for Production Plan (Work Orders)
 * Work Orders represent discrete units of work for both manufacturing and distribution operations.
 * (Formerly called Manufacturing Orders)
 */
import {
	CreateManufacturingOrderDto,
	CreateWorkOrderDto,
	ManufacturingOrder,
	UpdateManufacturingOrderDto,
	UpdateWorkOrderDto,
	WorkOrder,
} from "../components/full-time-scheduler/production-plan/utils/calculations";
import { LineShift } from "../components/full-time-scheduler/production-plan/utils/line-shifts";
import axios from "../utils/api-client";

const API_BASE_URL = `${process.env.REACT_APP_API_END_POINT}/ProductionPlan`;

export interface WorkOrderAllocation {
	id: number;
	workOrderId: number;
	jobId?: string;
	workstation?: string;
	workersRequired: number;
	startTime: string;
	endTime: string;
	createdDate: string;
	modifiedDate?: string;
}

/** @deprecated Use WorkOrderAllocation instead */
export type MOAllocation = WorkOrderAllocation;

// AI Parsing Types
export interface AvailableProduct {
	id: number;
	sku: string;
	name: string;
}

export interface ExtractedWorkOrder {
	// Raw values from AI (what it saw in the document)
	rawProduct: string;
	rawQuantity: string;
	rawWorkstation: string;
	rawStartTime: string;
	rawEndTime: string;

	// Matched/parsed values
	productId?: number;
	productName: string;
	sku: string;
	quantity?: number;
	workstation: string;
	startTime: string;  // ISO 8601 format
	endTime: string;    // ISO 8601 format

	// Confidence score (0-100)
	confidence: number;

	// Validation
	isValid: boolean;
	validationErrors: string[];

	// Schedule uploads (a planner's grid or row list)
	/** Kept on the job order as its Notes attribute, e.g. "Dec Req · 1M". */
	notes?: string | null;
	/** Null for production; cleaning, changeover, maintenance, hold or setup for a block. */
	orderType?: string | null;
	/** exact, name, spelling or ai; null when the product was not matched. */
	productMatchedBy?: string | null;
	/** exact or name; null when the line was not matched. */
	workstationMatchedBy?: string | null;
	/** The planner's text on the run and where it was ("Prod Planning Import!D6"). */
	sourceText?: string | null;
	source?: string | null;
	batchSeq?: number | null;
	batchCount?: number | null;
	/** Worth a look but not blocking. */
	checks?: string[];
}

/** What a schedule upload read, skipped and needed help with. */
export interface ScheduleImportSummary {
	/** planning-grid or schedule-rows */
	format: string;
	sheet?: string;
	firstDay?: string;
	lastDay?: string;
	runsRead: number;
	actualRowsSkipped: number;
	historyRunsSkipped: number;
	earlierBatchesSkipped: number;
	numberOnlyCellsSkipped: number;
	blocksBuilt: number;
	batchOrdersBuilt: number;
	textsAskedAi: number;
	namesAskedAi: number;
	aiUsed: boolean;
}

/** @deprecated Use ExtractedWorkOrder instead */
export type ExtractedMO = ExtractedWorkOrder;

export interface AIParseResponse {
	success: boolean;
	extractedOrders: ExtractedWorkOrder[];
	processingTimeMs: number;
	warnings: string[];
	error?: string;
	
	// For multi-sheet Excel files - ask user to select
	requiresSheetSelection: boolean;
	availableSheets: string[];

	/** Set when the sheet was read as a planner's schedule rather than a plain table. */
	scheduleImport?: ScheduleImportSummary | null;
}

/**
 * A labor standard that could apply to a given work order, as returned by
 * ProductionPlan/{id}/labor-standards. Matched on product + workstation, or SKU.
 */
export interface AvailableLaborStandard {
	id: number;
	productId?: number;
	productName?: string;
	sku?: string;
	workstation: string;
	batchSize: number;
	totalDurationMinutes: number;
	description?: string;
	createdDate: string;
	/** Pre-composed "SKU - Workstation (N units)" label from the API */
	displayName: string;
	/** True when this is the standard currently attached to the order */
	isSelected: boolean;
}

export const productionPlanApi = {
	/**
	 * Get all work orders
	 */
	getAll: async (): Promise<WorkOrder[]> => {
		const response = await axios.get<WorkOrder[]>(API_BASE_URL);
		return response.data;
	},

	/**
	 * Get work orders for a date range
	 */
	getByDateRange: async (startDate: string, endDate: string): Promise<WorkOrder[]> => {
		const response = await axios.get<WorkOrder[]>(`${API_BASE_URL}/range`, {
			params: { startDate, endDate },
		});
		return response.data;
	},

	/**
	 * The batch label the next order of a product should carry, looked up
	 * plant-wide (so a week on screen that lacks the latest batch cannot
	 * cause a reused number).
	 */
	nextBatchLabel: async (productId: number): Promise<string> => {
		const response = await axios.get<{ label: string }>(`${API_BASE_URL}/next-batch-label`, { params: { productId } });
		return response.data.label;
	},

	/**
	 * Get a single work order by ID
	 */
	getById: async (id: number): Promise<WorkOrder> => {
		const response = await axios.get<WorkOrder>(`${API_BASE_URL}/${id}`);
		return response.data;
	},

	/**
	 * Create a new work order
	 */
	create: async (order: CreateWorkOrderDto): Promise<WorkOrder> => {
		const response = await axios.post<WorkOrder>(API_BASE_URL, order);
		return response.data;
	},

	/**
	 * Create multiple work orders (batch)
	 */
	createBatch: async (orders: CreateWorkOrderDto[]): Promise<WorkOrder[]> => {
		const response = await axios.post<WorkOrder[]>(`${API_BASE_URL}/batch`, orders);
		return response.data;
	},

	/**
	 * Update an existing work order
	 */
	update: async (id: number, order: UpdateWorkOrderDto): Promise<WorkOrder> => {
		// The server refuses an update whose body id does not match the route.
		const response = await axios.put<WorkOrder>(`${API_BASE_URL}/${id}`, { ...order, id });
		return response.data;
	},

	/**
	 * Delete a work order
	 */
	delete: async (id: number): Promise<void> => {
		await axios.delete(`${API_BASE_URL}/${id}`);
	},

	/**
	 * Delete all work orders for a date range
	 */
	deleteByDateRange: async (startDate: string, endDate: string): Promise<{ deletedCount: number }> => {
		const response = await axios.delete<{ deletedCount: number }>(`${API_BASE_URL}/range`, {
			params: { startDate, endDate },
		});
		return response.data;
	},

	/**
	 * Replace the saved plan for a week. The backend swaps out the orders that
	 * start inside the week and leaves other weeks — and other companies —
	 * untouched. Existing orders keep their id so they are updated, not
	 * duplicated.
	 */
	saveProductionPlan: async (
		weekStartDate: string,
		orders: (Omit<WorkOrder, "id"> & { id?: number })[]
	): Promise<void> => {
		await axios.post(`${API_BASE_URL}/save`, { weekStartDate, orders });
	},

	/**
	 * Get the labor standards that could apply to a work order
	 */
	getAvailableLaborStandards: async (
		id: number
	): Promise<AvailableLaborStandard[]> => {
		const response = await axios.get<AvailableLaborStandard[]>(
			`${API_BASE_URL}/${id}/labor-standards`
		);
		return response.data;
	},

	/**
	 * Attach a labor standard to a work order. Pass null to detach.
	 */
	updateLaborStandard: async (
		id: number,
		laborStandardId: number | null
	): Promise<void> => {
		await axios.put(`${API_BASE_URL}/${id}/labor-standard`, { laborStandardId });
	},

	/**
	 * Parse a document using AI to extract work orders
	 * Supports images (PNG, JPEG, WebP), PDFs, Excel, CSV, and Word documents
	 */
	/** The shifts each line of a plant works (its own crew's), plant-local, for the work plan. */
	getLineShifts: async (locationId: number, from: string, to: string): Promise<LineShift[]> => {
		const response = await axios.get<LineShift[]>(`${API_BASE_URL}/line-shifts`, { params: { locationId, from, to } });
		return response.data ?? [];
	},

	aiParse: async (
		file: File,
		weekStartDate: string,
		products: AvailableProduct[],
		workcenters: string[],
		selectedSheet?: string,
		/** The plant the upload starts from: plain line names are read as its lines first. */
		locationId?: number | null
	): Promise<AIParseResponse> => {
		const formData = new FormData();
		formData.append("file", file);
		formData.append("weekStartDate", weekStartDate);
		formData.append("products", JSON.stringify(products));
		formData.append("workcenters", JSON.stringify(workcenters));
		if (selectedSheet) {
			formData.append("selectedSheet", selectedSheet);
		}
		if (locationId) {
			formData.append("locationId", String(locationId));
		}

		const response = await axios.post<AIParseResponse>(
			`${API_BASE_URL}/ai-parse`,
			formData,
			{
				headers: {
					"Content-Type": "multipart/form-data",
				},
			}
		);
		return response.data;
	},
};

export default productionPlanApi;
